import { desktopApiOrigin } from "@/lib/release-channel";
// Shared `?next=` plumbing for the auth pages. The desktop sign-in flow
// (Tauri) opens the browser at the backend's
// `/api/v1/auth/desktop/authorize/?port=&state=&code_challenge=&
// code_challenge_method=` loopback endpoint, which 302s unauthenticated
// users to `/auth/login?next=<that-url-with-confirm=1>`. On success the
// login page must navigate the browser top-level back to that URL: the
// backend's 302 lands on `http://127.0.0.1:<port>/cb`, a cross-scheme hop a
// same-origin `fetch` cannot follow — see `navigateToExternalNext` and
// `isDesktopAuthorizeUrl` below.
//
// New users go through register → register-success → email link → verify-email
// → login. That path crosses tabs (the verification email opens in whatever
// browser context the user is in), so we persist `next` via localStorage to
// bridge the gap. Pages within the same tab pass it through React Router state
// + URL params.

const STORAGE_KEY = "denker.pendingDesktopNext";
const TTL_MS = 60 * 60 * 1000; // 1 hour

export const DESKTOP_HANDOFF_PATH = "/auth/desktop-handoff";

interface StoredNext {
  url: string;
  expiresAt: number;
}

// Permit `?next=` redirects only to same-origin paths or to the API host
// the app already talks to — anything else risks an open-redirect.
export function resolveSafeNext(rawNext: string | null): string | null {
  if (!rawNext) return null;
  try {
    const target = new URL(rawNext, window.location.origin);
    if (target.origin === window.location.origin) return target.toString();
    const apiOrigin =
      (import.meta.env.VITE_API_URL as string | undefined) ??
      (desktopApiOrigin() || undefined);
    if (apiOrigin && target.origin === new URL(apiOrigin).origin) {
      return target.toString();
    }
  } catch {
    // fall through
  }
  return null;
}

function isMacDesktop(): boolean {
  if (typeof navigator === "undefined") return false;
  const platform = navigator.platform || "";
  const ua = navigator.userAgent || "";
  const looksLikeMac = /Mac/.test(platform) || /Mac OS X/.test(ua);
  if (!looksLikeMac) return false;
  return (navigator.maxTouchPoints ?? 0) <= 1;
}

export function getDesktopHandoffNext(): string | null {
  if (typeof window === "undefined" || !isMacDesktop()) return null;
  return new URL(DESKTOP_HANDOFF_PATH, window.location.origin).toString();
}

export function resolveDesktopFirstNext(rawNext: string | null): string | null {
  return resolveSafeNext(rawNext) ?? getDesktopHandoffNext();
}

// Build a `/auth/<page>` link that carries the same `next` we're holding,
// so cross-page navigation inside the auth flow keeps the desktop bridge
// destination glued to the user.
export function withNext(path: string, next: string | null): string {
  if (!next) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}next=${encodeURIComponent(next)}`;
}

export function navigateToExternalNext(next: string): void {
  window.location.href = next;
}

// The desktop OAuth-loopback authorize endpoint. Matching on the full
// four-param shape (not just the path) keeps this from misfiring on some
// unrelated future `/api/v1/auth/desktop/authorize/` call that isn't the
// browser-consent hop. `confirm=1` marks the specific request that mints a
// one-time code and 302s to the desktop app's loopback listener — the hop
// that a `fetch` cannot follow cross-scheme and that a caller must
// therefore drive as a real top-level navigation via
// `navigateToExternalNext`.
export function isDesktopAuthorizeUrl(next: string): boolean {
  let target: URL;
  try {
    target = new URL(next, window.location.origin);
  } catch {
    return false;
  }
  return (
    target.pathname === "/api/v1/auth/desktop/authorize/" &&
    target.searchParams.has("port") &&
    target.searchParams.has("code_challenge") &&
    target.searchParams.get("confirm") === "1"
  );
}

// Stash the desktop bridge URL in localStorage so the post-verification
// "Sign in" click after the email round-trip can route back to it. Tab
// boundaries make sessionStorage useless here — the email link almost
// always opens in a fresh tab.
export function stashPendingNext(next: string | null): void {
  if (!next) return;
  const safe = resolveSafeNext(next);
  if (!safe) return;
  try {
    const entry: StoredNext = { url: safe, expiresAt: Date.now() + TTL_MS };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // storage may be disabled (private mode); ignore — user falls back to
    // re-triggering desktop sign-in after verification.
  }
}

// One-shot read: returns the stored next URL if still valid and clears
// the entry. Validates the URL again to defend against a stale entry
// from a previous build that pointed at a now-disallowed origin.
export function popPendingNext(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    localStorage.removeItem(STORAGE_KEY);
    const entry = JSON.parse(raw) as StoredNext;
    if (!entry?.url || !entry?.expiresAt) return null;
    if (Date.now() > entry.expiresAt) return null;
    return resolveSafeNext(entry.url);
  } catch {
    return null;
  }
}
